"use client";

import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { EditWorkingHourFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import Buttons from "@/components/core/Buttons";
import { WorkingHours } from "@/actions/types";
import hooks from "@/hooks";
import { TimePicker } from "@/components/ui/datetime-picker";
import { format, parse } from "date-fns";
import { Label } from "@/components/ui/label";
import { WeekDayEnum } from "@/enum";

interface IEditWorkingHourForm {
  closeModal: (isOpen: boolean) => void;
  workingHour: WorkingHours;
  handleAfterEditWorkingHour: (updatedWorkingHour: WorkingHours) => void;
}

type WorkingHour = z.infer<typeof EditWorkingHourFormSchema>;

const EditWorkingHourForm = ({
  closeModal,
  workingHour,
  handleAfterEditWorkingHour,
}: IEditWorkingHourForm) => {
  const { toast } = useToast();

  const form = useForm<WorkingHour>({
    resolver: zodResolver(EditWorkingHourFormSchema),
    defaultValues: {
      open_at: workingHour.open_at,
      close_at: workingHour.close_at,
    },
  });

  const updateWorkingHourService = hooks.useUpdateSalonWorkingHour({
    onSuccess: (data) => {
      toast({
        title: "Horário de trabalho atualizado com sucesso",
        description: `O horário de trabalho de ${WeekDayEnum[workingHour.week_day as keyof typeof WeekDayEnum]} foi atualizado com sucesso`,
        variant: "success",
      });
      handleAfterEditWorkingHour(data);
      closeModal(false);
    },
    onError: (error) => {
      toast({
        title: "Falha ao atualizar horário de trabalho",
        description: "Não foi possível atualizar o horário de trabalho",
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: WorkingHour) {
    await updateWorkingHourService.mutateAsync({
      id: workingHour.id,
      open_at: data.open_at,
      close_at: data.close_at,
    });
  }

  const openAt = form.watch().open_at;
  const closeAt = form.watch().close_at;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className=" w-full flex flex-col items-center gap-y-6"
      >
        <h2 className="text-label-lg">
          {WeekDayEnum[workingHour.week_day as keyof typeof WeekDayEnum]}
        </h2>
        <div className="w-full grid grid-cols-1 gap-4 items-center">
          <div className="w-full flex items-center justify-between gap-4">
            <Label>Abertura</Label>
            <TimePicker
              key={`open_at_${workingHour.id}-${openAt}`}
              granularity="minute"
              date={parse(
                openAt.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
                "HH:mm",
                new Date(),
              )}
              onChange={(date) => {
                if (date) {
                  const formattedTime = format(date, "HHmm");
                  form.setValue("open_at", formattedTime);
                }
              }}
            />
          </div>
          {form.formState.errors.open_at && (
            <h3>{form.formState.errors.open_at.message}</h3>
          )}

          <div className="w-full flex items-center justify-between gap-4">
            <Label>Fechamento</Label>
            <TimePicker
              key={`close_at${workingHour.id}-${closeAt}`}
              granularity="minute"
              date={parse(
                closeAt.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
                "HH:mm",
                new Date(),
              )}
              onChange={(date) => {
                if (date) {
                  const formattedTime = format(date, "HHmm");
                  form.setValue("close_at", formattedTime);
                }
              }}
            />
          </div>
        </div>

        <div className="w-full flex items-center gap-4 mt-4">
          <Buttons.DefaultButton
            title={"Confirmar"}
            type="button"
            onClick={() => {
              onSubmit(form.getValues());
            }}
            isLoading={updateWorkingHourService.isPending}
          />
          <Buttons.DefaultButton
            variant="secondary"
            title={"Cancelar"}
            type="button"
            onClick={() => closeModal(false)}
          />
        </div>
      </form>
    </Form>
  );
};

export default EditWorkingHourForm;
